import { useEffect, useState } from 'react';

import { DBExtractTestCase } from '@core/types/types.db';
import {
  TesterExtractAddEndpoint,
  TesterExtractListEndpoint,
  TesterExtractRunEndpoint,
} from '@core/types/types.endpoint.tester';
import { Button } from '@frontend/components/ui/button';
import { Checkbox } from '@frontend/components/ui/checkbox';
import { useApi } from '@frontend/shared/hooks/shared.hook.useApi';
import { useInterval } from '@frontend/shared/hooks/shared.hook.useInterval';
import { useSubmitter } from '@frontend/shared/hooks/useSubmitter';
import { callApi } from '@frontend/shared/util/util.callApi';
import { TesterExtractItem } from '@frontend/Tester/Extract/TesterExtractItem';

export function TesterExtractPage() {
  const [lastCheckedAt, setLastCheckedAt] = useState<number | undefined>(
    undefined,
  );
  const [results, setResults] = useState<DBExtractTestCase[]>([]);
  const [extract, setExtract] = useState(true);
  const { data, refetch } = useApi<TesterExtractListEndpoint>(
    '/tester/extract/list',
    {},
  );

  useEffect(() => {
    if (data) {
      setResults((prev) =>
        [
          ...prev.map((p) => {
            const d = data.test_cases.find((d) => d.ts === p.ts);
            return d || p;
          }),
          ...data.test_cases.filter((d) => !prev.some((p) => p.ts === d.ts)),
        ].sort((a, b) => a.ts.localeCompare(b.ts)),
      );
      setLastCheckedAt(data.last_checked_at);
    }
  }, [data]);

  useInterval(() => {
    refetch({ last_updated_at: lastCheckedAt });
  }, 5000);

  const runAll = useSubmitter(async (target: 'status_quo' | 'experiment') => {
    await callApi<TesterExtractRunEndpoint>('/tester/extract/run', {
      ts: results.map((d) => d.ts),
      target,
      skip_extract: !extract,
    });
    refetch({ last_updated_at: lastCheckedAt });
  });

  const adder = useSubmitter(async () => {
    await callApi<TesterExtractAddEndpoint>('/tester/extract/add', {});
    refetch({ last_updated_at: lastCheckedAt });
  });

  return (
    <div className="w-full my-8 mx-auto max-w-[1920px] p-2">
      <div className="flex">
        <div className="text-xl font-medium flex flex-wrap items-center w-2/5 p-3 pl-[60px] flex-none max-w-[720px]">
          Chat history
          <Button
            className="ml-4"
            size="sm"
            onClick={adder.submit}
            disabled={adder.isSubmitting}
          >
            Add
          </Button>
          <div className="flex items-center ml-4">
            <Checkbox
              checked={extract}
              onCheckedChange={(checked) => setExtract(checked === true)}
              id="extract"
            />
            <label htmlFor="extract" className="ml-2 text-sm">
              Extract
            </label>
          </div>
          <Button
            className="ml-4"
            size="sm"
            onClick={() => runAll.submit('status_quo')}
          >
            Run Left
          </Button>
          <Button
            className="ml-4"
            size="sm"
            onClick={() => runAll.submit('experiment')}
          >
            Run Right
          </Button>
        </div>
        <h2 className="text-xl font-medium w-full p-3">Status quo</h2>
        <h2 className="text-xl font-medium w-full p-3">Results</h2>
      </div>

      {results
        .map((d, i) => (
          <TesterExtractItem
            refetch={() => refetch({ last_updated_at: lastCheckedAt })}
            key={d.ts}
            item={d}
            index={i}
            onSave={(item) => {
              setResults(results.map((d) => (d.ts === item.ts ? item : d)));
            }}
            extract={extract}
          />
        ))
        .reverse()}
    </div>
  );
}
